import { Button, ButtonGroup, Container, Step, StepLabel, Stepper, Typography } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react"; // ? CHANGED: added useRef earlier
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { BrandEnum } from "../_common/enum/brand.enum";
import { FormTemplateTypeEnum } from "../_common/enum/forms/formTemplateType.enum";
import { IFormManage } from "../_common/models/form/formManage.model";
import { LoaderDoubleLines } from "../_components/loader/LoaderDoubleLines";
import { quoteTypeOrder } from "../_const/quoteTypeDisplayOrder.const";
import { splitWordOnCamelCase } from "../_helpers/format.helper";
import { handleErrorResponse } from "../_helpers/handleResponse.helper";
import { retryHeight, sendContainerHeightToParent, sendHeightToParent, sendScrollMessageToParent } from "../_helpers/iFrameSize.helper";
import { withFormAccessToken } from "../_helpers/formAccessToken.helper";
import { formService } from "../_services/form.service";
import { FormSectionManage } from "./formSection/formSection-manage";

export function FormManage(params: {
    setIsSaved?: () => any,
    brand: BrandEnum
}) {

    const { formId, clientId: clientIdParam } = useParams<{ formId: string; clientId?: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const existingClientId = useMemo(() => {
        const fromParam = Number(clientIdParam);
        if (fromParam > 0) return fromParam;
        const fromState = Number(location.state?.clientId);
        if (fromState > 0) return fromState;
        return null;
    }, [clientIdParam, location.state?.clientId]);

    const [data, setData] = useState<IFormManage>();

    const [isLoading, setLoading] = useState(false);
    const [formChangeLoading, setFormChangeLoading] = useState(true);
    const [initLoad, setInitLoad] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    const [activeStep, setActiveStep] = useState<number | null>(null);
    const [reloadCounter, setReloadCounter] = useState(0);

    const [stepWithLatestId, setStepWithLatestId] = useState<number | null>(null);


    useEffect(() => {
        if (data && data.formTemplateTypeId === FormTemplateTypeEnum.Mortgages) return;
        if (location.state?.stateCounter && location.state?.stateCounter > 0 && activeStep != 0) {
            setActiveStep(0)
            // retryHeight(sendContainerHeightToParent);
        }

    }, [location.state?.stateCounter]);

    useEffect(() => {
        if (activeStep === 2) {
            setActiveStep(1)
        }
    }, [activeStep]);

    useEffect(() => {
        setFormChangeLoading(true);
        fetchData();

    }, [formId]);

    useEffect(() => {
        if (data) {

            if (data.id !== data.oldFormId) {
                setActiveStep(0);
                setReloadCounter(r => ++r);

            }
            else if ((activeStep === 0 || shouldShowReviewSection()) && !(location.state?.stateCounter || 0 > 0)) {
                resetActiveStep();
            }

            resetStepWithLatestId();

            if (data.id === data.oldFormId) {
                sendScrollMessageToParent();   // NEW
            }

            // Wait for form to fully load
            if (!initLoad) {
                retryHeight(sendHeightToParent);
            }
            else {
                setInitLoad(false);
            }
        }
    }, [data]);


    //useEffect(() => {
    //    if (activeStep === null) return;

    //    window.scrollTo({
    //        top: 0,
    //        left: 0,
    //        behavior: "smooth",
    //    });

    //    try {
    //        sendScrollMessageToParent();
    //    } catch {
    //        // ignore if no parent listener
    //    }
    //}, [activeStep]);

    ////more aggressive height recalculation
    //// runs on data, activeStep, and loading flags
    //// first sends exact height (shrink/grow)
    //// then retries after a short delay for layout/validation changes
    //useEffect(() => {
    //    if (!data) return;

    //    const updateHeightNow = () => {
    //        sendContainerHeightToParent();
    //    };

    //    const updateHeightWithRetry = () => {
    //        retryHeight(sendContainerHeightToParent);
    //    };

    //    // Immediately measure the new content (allows shrink)
    //    updateHeightNow();

    //    //retry after slight delay to catch async reflows
    //    const id = setTimeout(updateHeightWithRetry, 250);

    //    return () => clearTimeout(id);
    //}, [
    //    data,
    //    activeStep,
    //    formChangeLoading,
    //    isLoading,
    //    isSaving
    //]);

    //useEffect(() => {
    //    if (!data) return;
    //    retryHeight(sendContainerHeightToParent);
    //}, [data, activeStep]);

    function fetchData(isOnSave?: boolean) {

        setLoading(true);

        return formService
            .get(Number(formId), null)
            .then(
                (json) => {
                    setData((d: any) => ({ ...json, oldFormId: d?.id }));
                }
            )
            .finally(() => {
                setFormChangeLoading(false);
                setLoading(false);
                if (isOnSave == true) {
                    setIsSaving(false);
                }
            });
    }

    function onSave() {
        triggerNextStep(true);
    }

    function triggerNextStep(isOnSave?: boolean) {
        if (activeStep == getActiveStep()) {
            setActiveStep(activeStep + 1);
            setStepWithLatestId(((stepWithLatestId || 0) + 1));
        }
        else {
            setActiveStep(getActiveStep());
        }

        fetchData(isOnSave);
    }

    function getActiveStep() {
        let step: number | null = null;

        if (shouldShowReviewSection()) {
            return data?.formSectionList.length || 0;
        }

        if (data && data.formSectionList.length > 0) {
            data.formSectionList.forEach((item, index) => {
                if (!item.id && step === null) {
                    step = index;
                }
            })
        }
        return step || 0;
    }

    function resetActiveStep() {
        setActiveStep(getActiveStep());
    }

    function resetStepWithLatestId() {

        let step: number | null = null;
        if (data && data.formSectionList.length > 0) {
            data.formSectionList.forEach((item, index) => {
                if (item.id) {
                    step = index;
                }
            })
        }
        setStepWithLatestId(step);
    }

    function shouldShowReviewSection() {

        let shouldShowReview = false;
        if (data && data.formSectionList.length > 0) {
            shouldShowReview = !data.formSectionList.some((item, index) => {
                return !item.id
            })
        }
        return shouldShowReview;

    }

    function switchForms(formTemplateTypeId: FormTemplateTypeEnum) {

        if (data && formTemplateTypeId !== data.formTemplateTypeId) {

            // setActiveStep(null);
            setFormChangeLoading(true);
            setLoading(true);

            return formService.createByType(formTemplateTypeId, params.brand)
                .then(
                    ({ id, token }) => {
                        navigate(withFormAccessToken(`/forms/${id}`, id, token));
                    }, handleErrorResponse)
                .finally(() => {
                    setLoading(false);
                    retryHeight(sendHeightToParent);
                });
        }
        else if (data) {
            navigate(withFormAccessToken(`/forms/${formId}`, Number(formId)));
            retryHeight(sendHeightToParent);
        }
        else {
            return;
        }
    }

    const formTemplateSectionId = useMemo(() => {
        if (
            !data ||
            activeStep === null ||
            data.formSectionList.length === 0 ||
            activeStep >= data.formSectionList.length
        ) return 0;

        return data.formSectionList[activeStep].formTemplateSectionId;
    }, [activeStep, reloadCounter, data]);

    const formSectionId = useMemo(() => {
        if (
            !data ||
            activeStep === null ||
            data.formSectionList.length === 0 ||
            activeStep >= data.formSectionList.length
        ) return 0;

        return data.formSectionList[activeStep].id;
    }, [activeStep, reloadCounter, data]);

    return (
        <>
            <LoaderDoubleLines loading={formChangeLoading} />

            <Container
                id="formContainer"
                sx={{
                    marginBottom: 2,
                    backgroundColor: "#fff",
                    padding: 0,
                }}
                maxWidth={"xl"}
            >
                {(activeStep !== null && data && data.formSectionList.length > 0) &&
                    <>
                        <ButtonGroup
                            // sx={{ width: "100%" }}
                            variant="outlined"
                            aria-label="outlined primary button group"
                        >
                            {quoteTypeOrder
                                .map((numericValue) => {
                                    return (
                                        <Button
                                            fullWidth
                                            key={numericValue}
                                            onClick={() => switchForms(numericValue)}
                                            sx={(theme) => ({
                                                backgroundColor:
                                                    data.formTemplateTypeId === numericValue
                                                        ? theme.palette.primary.main
                                                        : "transparent",
                                                color:
                                                    data.formTemplateTypeId === numericValue
                                                        ? theme.palette.getContrastText(theme.palette.primary.main)
                                                        : theme.palette.primary.main,
                                                "&:hover": {
                                                    backgroundColor:
                                                        data.formTemplateTypeId === numericValue
                                                            ? theme.palette.primary.dark
                                                            : "rgba(0, 0, 0, 0.08)"
                                                },
                                            })}
                                        >
                                            {splitWordOnCamelCase(FormTemplateTypeEnum[numericValue])}
                                        </Button>
                                    );
                                })}
                        </ButtonGroup>

                        <Typography
                            variant="h3"
                            textAlign="center"
                            textTransform="uppercase"
                            color="primary"
                            fontWeight="550"
                        >
                            {data.displayName ? data.displayName : data.name}
                        </Typography>

                        <Stepper activeStep={activeStep} alternativeLabel>
                            {data.formSectionList.map((listItem, index) => {
                                const stepProps: { completed?: boolean } = {};

                                stepProps.completed = (listItem.id || 0) > 0 || index < activeStep;

                                return (
                                    <Step key={index + "_" + listItem.id} {...stepProps} sx={{ padding: 0 }}>
                                        <StepLabel>
                                            <Button
                                                variant={activeStep === index ? "contained" : "outlined"}
                                                disabled={
                                                    stepWithLatestId === null ||
                                                    (!listItem.id && (stepWithLatestId + 1) !== index)
                                                }
                                                onClick={() => { setActiveStep(index) }}
                                            >
                                                {listItem.name}
                                            </Button>
                                        </StepLabel>
                                    </Step>
                                );
                            })}
                        </Stepper>
                    </>
                }

                {(activeStep !== data?.formSectionList.length) &&
                    <FormSectionManage
                        formTemplateSectionId={formTemplateSectionId}
                        isSectionSaving={isSaving}
                        formSectionId={formSectionId}
                        onSave={onSave}
                        isFormCompleted={!!data?.dateCompleted}
                        activeStep={activeStep}
                        formTemplateName={data?.name}
                        formTemplateTypeId={data?.formTemplateTypeId}
                        brand={params.brand}
                        existingClientId={existingClientId}
                    />
                }

            </Container>
        </>
    );
}
